/* eslint-disable camelcase */
import { VisibilityOffOutlined, VisibilityOutlined } from '@mui/icons-material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import {
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  Paper,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import { CountryData } from 'react-phone-input-2'
import 'react-phone-input-2/lib/material.css'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../../_helpers/hooks'
import { isBusinessURL } from '../../../_helpers/routes'
import LanguageSelector from '../../../components/languageSelector'
import PhoneNumberInput from '../../../components/phoneInput'
import CopyRight from '../../../features/copyRight/CopyRight'
import PasswordInfo from '../../../features/passwordValidationInfo'
import Logo from '../../../images/logo.svg'
import { authenticationState, signUp } from '../../../slices/authenticationSlice'
import { translationData } from '../../../slices/translationSlice'
import './Phone-Input.scss'

const SignUpPage = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const { t } = useTranslation()
  const [showPassword, setShowPassword] = useState(false)
  const [countryData, setCountryData] = useState<CountryData | Record<string, never>>()

  type FormInputs = {
    name: string
    email: string
    phone: string
    password: string
  }

  const handleClickShowPassword = () => {
    setShowPassword((prevValue) => !prevValue)
  }
  const {
    handleSubmit,
    control,
    formState: { isSubmitted },
    setError,
  } = useForm<FormInputs>({
    mode: 'onChange',
  })
  const translationState = useAppSelector(translationData)
  const { selectedLanguage } = translationState
  const onSubmit: SubmitHandler<FormInputs> = (data) => {
    if (countryData?.dialCode !== undefined) {
      const modifiedSignUpFormData = {
        ...data,
        phone: data.phone.replace(countryData.dialCode, ''),
        country_code: countryData.dialCode,
        iso_code: countryData?.countryCode,
        role_id: 'CUSTOMER',
        translationLanguage: selectedLanguage,
      }
      dispatch(signUp(modifiedSignUpFormData))
    }
  }

  const handleChange = (data: CountryData | Record<string, never>) => {
    setCountryData(data)
  }

  const { signUpFormErrors } = useAppSelector(authenticationState)
  useEffect(() => {
    if (signUpFormErrors.message) {
      for (const i in signUpFormErrors.message) {
        setError(i as 'name' | 'email' | 'password' | 'phone', {
          message: signUpFormErrors.message[i][0],
        })
      }
    }
  }, [setError, signUpFormErrors])

  const handleClick = (label: string) => {
    const isBusiness = isBusinessURL()
    const baseBusinessUrl = `${window.location.protocol}//${process.env.REACT_APP_BUSINESS_URL}`
    let path = ''
    switch (label) {
      case 'termsOfService':
        path = 'termsofservice'
        break
      case 'privacy-policy':
        path = 'privacy-policy'
        break
    }

    const url = isBusiness ? `${baseBusinessUrl}/${path}` : `/${path}`
    isBusiness ? window.location.replace(url) : navigate(url)
  }

  return (
    <Grid item xs={12} sm={6} md={5} component={Paper} elevation={0} square>
      <Grid
        container
        direction='column'
        alignItems='center'
        height='100%'
        justifyContent='center'
        sx={{
          mx: { xs: 'auto' },
          p: { xs: 4, sm: 4, md: 5, lg: 10 },
          maxWidth: '90%',
        }}
      >
        <Grid item sx={{ flexGrow: 1 }}>
          <Grid
            container
            direction='column'
            alignItems='center'
            height='100%'
            justifyContent='center'
          >
            <Grid item>
              <Box
                component='img'
                sx={{
                  height: 33,
                  marginBottom: { xs: 5, md: 4 },
                  cursor: 'pointer',
                }}
                alt={t('signUp.logoText')}
                src={Logo}
                onClick={() =>
                  isBusinessURL()
                    ? window.location.replace(
                        `${window.location.protocol}//${process.env.REACT_APP_BUSINESS_URL}/`,
                      )
                    : navigate('/')
                }
              />
              <Typography variant='h2' sx={{ mb: 2 }}>
                {t('signUp.signUp')}
              </Typography>

              <Box component={'span'} color='secondary.main'>
                <Typography variant='subtitle1' sx={{ mt: 2 }}>
                  {t('signUp.signUpText', { userType: 'customer' })}
                </Typography>
              </Box>
              <form noValidate onSubmit={handleSubmit(onSubmit)}>
                <Box sx={{ mt: 1 }}>
                  <Controller
                    name='name'
                    control={control}
                    defaultValue=''
                    rules={{
                      required: t('signUp.fullNameRequired'),
                      minLength: {
                        value: 1,
                        message: t('signUp.fullNameMinMax'),
                      },
                      maxLength: {
                        value: 50,
                        message: t('signUp.fullNameMinMax'),
                      },
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        margin='normal'
                        fullWidth
                        label={t('signUp.fullName')}
                        error={isSubmitted && !!error}
                        helperText={isSubmitted && error ? error.message : null}
                        aria-label={t('signUp.fullName')}
                      />
                    )}
                  />

                  <Box sx={{ mt: 1.5 }}>
                    <Controller
                      name='phone'
                      control={control}
                      defaultValue=''
                      rules={{
                        required: t('signUp.phoneNumberRequired'),
                        pattern: {
                          value: /^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4,9}$/,
                          message: `${t('signUp.phoneErrorMsg')}`,
                        },
                      }}
                      render={({ field, fieldState: { error } }) => (
                        <Box component='div' className={error && isSubmitted ? 'error' : ''}>
                          <PhoneNumberInput
                            style={{ width: '100%' }}
                            phoneField={{
                              name: field.name,
                              value: field.value,
                              onBlur: field.onBlur,
                              isValid: !(isSubmitted && error?.message),
                              onChange: (value, data) => {
                                field.onChange(value)
                                handleChange(data)
                              },
                            }}
                            isMandatory={true}
                          />
                          {isSubmitted && error?.message ? (
                            <Typography variant='caption' color='error.main' sx={{ ml: 1.5 }}>
                              {error.message}
                            </Typography>
                          ) : null}
                        </Box>
                      )}
                    />
                  </Box>
                  <Controller
                    name='email'
                    control={control}
                    defaultValue=''
                    rules={{
                      required: t('signUp.emailAddressRequired'),
                      maxLength: {
                        value: 64,
                        message: t('signUp.emailMaxLengthError'),
                      },
                      pattern: {
                        value: /^[a-zA-Z0-9._+]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)+$/,
                        // production_value_: /^[a-zA-Z0-9._]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)+$/
                        // value: /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                        message: t('signUp.emailValid'),
                      },
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        margin='normal'
                        fullWidth
                        label={t('signUp.emailPlaceholder')}
                        error={isSubmitted && !!error}
                        helperText={isSubmitted && error ? error.message : null}
                        aria-label={t('signUp.emailPlaceholder')}
                      />
                    )}
                  />

                  <Controller
                    name='password'
                    control={control}
                    defaultValue=''
                    rules={{
                      required: t('signUp.passwordRequired'),
                      pattern: {
                        value: /^(?=.*\d)(?=.*[!@#$%^&*])(?=.*[a-z])(?=.*[A-Z]).{8,}$/,
                        message: t('signUp.validPasswordRequired'),
                      },
                      minLength: {
                        value: 8,
                        message: t('signUp.passwordMinCharacterError'),
                      },
                      maxLength: {
                        value: 16,
                        message: t('signUp.passwordMaxCharacterError'),
                      },
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        margin='dense'
                        fullWidth
                        label={t('signUp.passwordLabel')}
                        type={!showPassword ? 'password' : 'text'}
                        error={isSubmitted && !!error}
                        helperText={isSubmitted && error ? error.message : null}
                        aria-label={t('signUp.passwordLabel')}
                        InputProps={{
                          endAdornment: (
                            <>
                              <InputAdornment position='end'>
                                <IconButton
                                  aria-label='toggle password visibility'
                                  onClick={handleClickShowPassword}
                                  edge='end'
                                  sx={{ color: 'secondary.light', mr: '2px' }}
                                >
                                  {showPassword ? (
                                    <VisibilityOutlined />
                                  ) : (
                                    <VisibilityOffOutlined />
                                  )}
                                </IconButton>
                              </InputAdornment>
                              <InputAdornment position='start'>
                                {field.value && !error ? (
                                  <IconButton>
                                    <CheckCircleOutlineIcon fontSize='small' color='success' />
                                  </IconButton>
                                ) : (
                                  <PasswordInfo />
                                )}
                              </InputAdornment>
                            </>
                          ),
                        }}
                      />
                    )}
                  />

                  <Grid container>
                    <Grid
                      container
                      item
                      sx={{ justifyContent: 'center', textAlign: 'center', mt: 3 }}
                    >
                      <Typography variant='subtitle2'>
                        <Trans
                          i18nKey='signUp.termsAgreementText'
                          components={{
                            link1: (
                              <Link
                                underline='hover'
                                onClick={() => handleClick('termsOfService')}
                              />
                            ),
                            link2: (
                              <Link
                                underline='hover'
                                onClick={() => handleClick('privacy-policy')}
                              />
                            ),
                          }}
                        />
                      </Typography>
                    </Grid>
                  </Grid>
                  <Button
                    type='submit'
                    fullWidth
                    variant='contained'
                    sx={{ mt: 3, mb: 2 }}
                    size='large'
                    aria-label={t('signUp.signUp')}
                  >
                    <Typography variant='h5'>{t('signUp.signUp')}</Typography>
                  </Button>
                  <Grid container>
                    <Grid container item sx={{ justifyContent: 'center', textAlign: 'center' }}>
                      <Typography variant='body2'>
                        {t('signUp.registrationPageLoginLinkText')}
                        &nbsp;
                        <Link
                          role='link'
                          tabIndex={0}
                          variant='body2'
                          underline='hover'
                          sx={{
                            '&:focus-visible': {
                              outline: `0.0625rem solid ${theme.palette.error.main}`,
                              borderRadius: '0.25rem',
                              padding: '0.3rem',
                            },
                          }}
                          onClick={() =>
                            isBusinessURL()
                              ? window.location.replace(
                                  `${window.location.protocol}//${process.env.REACT_APP_BUSINESS_URL}/login`,
                                )
                              : navigate('/login')
                          }
                          aria-label={t('signUp.signIn')}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter')
                              isBusinessURL()
                                ? window.location.replace(
                                    `${window.location.protocol}//${process.env.REACT_APP_BUSINESS_URL}/login`,
                                  )
                                : navigate('/login')
                          }}
                        >
                          {t('signUp.signIn')}
                        </Link>
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </form>
            </Grid>
          </Grid>
        </Grid>
        <Grid mt={3} mb={2}>
          <LanguageSelector />
        </Grid>
        <Grid item>
          <CopyRight sx={{ mb: 2 }} />
        </Grid>
      </Grid>
    </Grid>
  )
}

export default SignUpPage
